import { call, put, takeLatest } from 'redux-saga/effects';
import type {
  LoginResponse,
  OtpDeliveryMethod,
  OtpResponse,
  RegisterResponse,
} from '@/types/auth.types';
import {
  GENERATE_OTP_FAILURE,
  GENERATE_OTP_REQUEST,
  GENERATE_OTP_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  REGISTER_REQUEST,
  REGISTER_SUCCESS,
  REGISTER_FAILURE,
  VERIFY_OTP_FAILURE,
  VERIFY_OTP_REQUEST,
  VERIFY_OTP_SUCCESS,
  type AuthActionTypes,
} from '@/store/actions/authActions';
import {
  generateOtp,
  loginUser,
  registerUser,
  verifyLoginOtp,
} from '@/services/authApi';
import { setTokens } from '@/api/session';

function* registerSaga(action: Extract<AuthActionTypes, { type: typeof REGISTER_REQUEST }>) {
  try {
    const response: RegisterResponse = yield call(registerUser, action.payload);
    yield put({
      type: REGISTER_SUCCESS,
      payload: response.message || 'Registration successful',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Registration failed';
    yield put({
      type: REGISTER_FAILURE,
      payload: errorMessage,
    });
  }
}

function* loginSaga(action: Extract<AuthActionTypes, { type: typeof LOGIN_REQUEST }>) {
  try {
    // console.log('[authSaga] Login request', action.payload);
    const response: LoginResponse & Record<string, unknown> = yield call(loginUser, action.payload);
    // console.log('[authSaga] Login response', JSON.stringify(response, null, 2));

    const rawData =
      (typeof response.data === 'object' && response.data !== null
        ? response.data
        : {}) as Record<string, unknown>;

    const transactionId =
      (rawData.transactionId as string | undefined) ||
      (response.transactionId as string | undefined) ||
      (rawData.otpTransactionId as string | undefined) ||
      (response.otpTransactionId as string | undefined) ||
      '';

    const mobileNumber =
      (rawData.mobileNumber as string | undefined) ||
      (response.mobileNumber as string | undefined) ||
      action.payload.mobileNumber ||
      '';

    const email =
      (rawData.email as string | undefined) ||
      (response.email as string | undefined) ||
      null;

    const deliveryMethod =
      ((rawData.deliveryMethod ?? response.deliveryMethod) as OtpDeliveryMethod | undefined) ||
      null;

    if (!transactionId) {
      const missingTxMessage = 'Login response missing OTP transaction. Please try again.';
      // console.error('[authSaga]', missingTxMessage, response);
      yield put({
        type: LOGIN_FAILURE,
        payload: missingTxMessage,
      });
      return;
    }

    yield put({
      type: LOGIN_SUCCESS,
      payload: {
        message: response.message || 'Login successful',
        mobileNumber,
        email,
        transactionId,
        deliveryMethod,
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Login failed';
    // console.error('[authSaga] Login error', errorMessage);
    yield put({
      type: LOGIN_FAILURE,
      payload: errorMessage,
    });
  }
}

function* generateOtpSaga(
  action: Extract<AuthActionTypes, { type: typeof GENERATE_OTP_REQUEST }>
) {
  try {
    const response: OtpResponse = yield call(generateOtp, action.payload);
    yield put({
      type: GENERATE_OTP_SUCCESS,
      payload: {
        message: response.message || 'OTP generated successfully',
        maskedDestination: response.data?.maskedDestination ?? '',
        deliveryMethod: response.data?.deliveryMethod ?? action.payload.deliveryMethod,
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to generate OTP';
    yield put({
      type: GENERATE_OTP_FAILURE,
      payload: errorMessage,
    });
  }
}

function* verifyOtpSaga(action: Extract<AuthActionTypes, { type: typeof VERIFY_OTP_REQUEST }>) {
  try {
    // console.log('[authSaga] Verify OTP request', action.payload);
    const response: OtpResponse = yield call(verifyLoginOtp, action.payload);
    // console.log('[authSaga] Verify OTP response', JSON.stringify(response, null, 2));

    if (response.success === false) {
      const message = response.message || 'OTP verification failed';
      // console.error('[authSaga] Verify OTP failed', message);
      yield put({
        type: VERIFY_OTP_FAILURE,
        payload: message,
      });
      return;
    }

    const accessToken = response.data?.accessToken ?? null;
    const refreshToken = response.data?.refreshToken ?? null;
    const customerId = response.data?.customerId ?? response.data?.userId ?? null;

    setTokens(accessToken ?? undefined, refreshToken ?? undefined);
    if (customerId) {
      localStorage.setItem('customerId', customerId);
    }

    yield put({
      type: VERIFY_OTP_SUCCESS,
      payload: {
        message: response.message || 'OTP verified successfully',
        accessToken,
        refreshToken,
        customerId,
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'OTP verification failed';
    // console.error('[authSaga] Verify OTP error', errorMessage);
    yield put({
      type: VERIFY_OTP_FAILURE,
      payload: errorMessage,
    });
  }
}

export default function* watchAuthSaga() {
  yield takeLatest(REGISTER_REQUEST, registerSaga);
  yield takeLatest(LOGIN_REQUEST, loginSaga);
  yield takeLatest(GENERATE_OTP_REQUEST, generateOtpSaga);
  yield takeLatest(VERIFY_OTP_REQUEST, verifyOtpSaga);
}
